import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const CartContext = createContext();

// localStorage에서 장바구니 데이터를 초기값으로 불러오는 함수
function getInitialCartItems() {
  if (typeof window === 'undefined') {
    return [];
  }
  
  try {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      return JSON.parse(savedCart);
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to load cart from localStorage:', error);
    }
  }
  
  return [];
}

export function CartProvider({ children }) {
  // 초기 상태를 localStorage에서 바로 읽어오기
  const [cartItems, setCartItems] = useState(getInitialCartItems);

  // localStorage에 장바구니 데이터 저장
  useEffect(() => {
    try {
      localStorage.setItem('cart', JSON.stringify(cartItems));
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save cart to localStorage:', error);
      }
    }
  }, [cartItems]);

  // 🚀 useCallback으로 함수 메모이제이션 (리렌더링 최소화)
  const addToCart = useCallback((product, option = null, quantity = 1) => {
    const itemOption = option || '기본';
    
    setCartItems((prev) => {
      // 같은 상품(productId)과 같은 옵션을 가진 아이템이 있는지 확인
      const existingItemIndex = prev.findIndex(
        (item) => item.productId === product.id && item.option === itemOption
      );

      if (existingItemIndex !== -1) {
        // 기존 아이템이 있으면 수량만 증가
        const updatedItems = [...prev];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + quantity,
        };
        return updatedItems;
      } else {
        // 기존 아이템이 없으면 새로 추가
        const newItem = {
          id: `${product.id}-${Date.now()}`,
          productId: product.id,
          name: product.name,
          description: product.description,
          price: product.price,
          originalPrice: product.originalPrice,
          image: product.image,
          option: itemOption,
          quantity: quantity,
          selected: true,
        };
        return [...prev, newItem];
      }
    });
  }, []);

  const removeFromCart = useCallback((itemId) => {
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  }, []);

  const updateQuantity = useCallback((itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(itemId);
      return;
    }
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  }, [removeFromCart]);

  const toggleSelect = useCallback((itemId) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, selected: !item.selected } : item
      )
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    setCartItems((prev) => {
      const allSelected = prev.every((item) => item.selected);
      return prev.map((item) => ({ ...item, selected: !allSelected }));
    });
  }, []);

  const removeSelected = useCallback(() => {
    setCartItems((prev) => prev.filter((item) => !item.selected));
  }, []);

  const getSelectedItems = useCallback(() => {
    return cartItems.filter((item) => item.selected);
  }, [cartItems]);

  const getTotalPrice = useCallback(() => {
    return cartItems
      .filter((item) => item.selected)
      .reduce((total, item) => total + item.price * item.quantity, 0);
  }, [cartItems]);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  // 🚀 Context value 메모이제이션 (불필요한 리렌더링 방지)
  const value = useMemo(() => ({
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    toggleSelect,
    toggleSelectAll,
    removeSelected,
    getSelectedItems,
    getTotalPrice,
    clearCart,
  }), [cartItems, addToCart, removeFromCart, updateQuantity, toggleSelect, toggleSelectAll, removeSelected, getSelectedItems, getTotalPrice, clearCart]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

